import { assertOk, readSSE } from "../stream";
import type {
  ChatMessage,
  ChatRequest,
  CompletionResult,
  LLMProvider,
  StreamChunk,
  ToolCall,
} from "../types";

export interface AnthropicOptions {
  baseUrl: string;
  apiKey: string;
  model: string;
  maxTokens: number;
  anthropicVersion: string;
  extraHeaders?: Record<string, string>;
}

interface AnthropicBlock {
  type: string;
  text?: string;
  id?: string;
  name?: string;
  input?: Record<string, unknown>;
}

/**
 * Anthropic Messages API, called directly from the browser.
 *
 * Direct browser access requires the `anthropic-dangerous-direct-browser-access`
 * header — the request carries the user's own key, which they configured
 * locally. There is no backend of ours in the path.
 */
export class AnthropicProvider implements LLMProvider {
  readonly kind = "anthropic" as const;
  readonly supportsTools = true;

  constructor(private readonly opts: AnthropicOptions) {}

  private headers(): Record<string, string> {
    return {
      "content-type": "application/json",
      "x-api-key": this.opts.apiKey,
      "anthropic-version": this.opts.anthropicVersion,
      "anthropic-dangerous-direct-browser-access": "true",
      ...this.opts.extraHeaders,
    };
  }

  private endpoint(): string {
    return `${this.opts.baseUrl.replace(/\/$/, "")}/v1/messages`;
  }

  private body(req: ChatRequest, stream: boolean): Record<string, unknown> {
    const system = collectSystem(req);
    const body: Record<string, unknown> = {
      model: req.model,
      max_tokens: req.maxTokens,
      stream,
      messages: toAnthropicMessages(req.messages),
    };
    if (system) body.system = system;
    if (req.tools?.length) {
      body.tools = req.tools.map((t) => ({
        name: t.name,
        description: t.description,
        input_schema: t.parameters,
      }));
    }
    return body;
  }

  async *stream(
    req: ChatRequest,
    signal?: AbortSignal,
  ): AsyncGenerator<StreamChunk> {
    const res = await assertOk(
      await fetch(this.endpoint(), {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify(this.body(req, true)),
        signal,
      }),
    );

    let finishReason: string | undefined;
    for await (const data of readSSE(res)) {
      if (data === "[DONE]") break;
      const evt = JSON.parse(data) as {
        type: string;
        delta?: { type?: string; text?: string; stop_reason?: string };
      };
      if (evt.type === "content_block_delta" && evt.delta?.type === "text_delta") {
        yield { type: "text", text: evt.delta.text ?? "" };
      } else if (evt.type === "message_delta" && evt.delta?.stop_reason) {
        finishReason = evt.delta.stop_reason;
      }
    }
    yield { type: "done", finishReason };
  }

  async complete(
    req: ChatRequest,
    signal?: AbortSignal,
  ): Promise<CompletionResult> {
    const res = await assertOk(
      await fetch(this.endpoint(), {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify(this.body(req, false)),
        signal,
      }),
    );
    const json = (await res.json()) as {
      content?: AnthropicBlock[];
      stop_reason?: string;
    };

    let text = "";
    const toolCalls: ToolCall[] = [];
    for (const block of json.content ?? []) {
      if (block.type === "text") text += block.text ?? "";
      else if (block.type === "tool_use") {
        toolCalls.push({
          id: block.id ?? cryptoId(),
          name: block.name ?? "",
          arguments: block.input ?? {},
        });
      }
    }
    return { text, toolCalls, finishReason: json.stop_reason };
  }
}

function collectSystem(req: ChatRequest): string {
  const parts: string[] = [];
  if (req.system) parts.push(req.system);
  for (const m of req.messages) if (m.role === "system") parts.push(m.content);
  return parts.join("\n\n");
}

function toAnthropicMessages(messages: ChatMessage[]): unknown[] {
  const out: unknown[] = [];
  for (const m of messages) {
    if (m.role === "system") continue;
    if (m.role === "tool") {
      out.push({
        role: "user",
        content: [
          {
            type: "tool_result",
            tool_use_id: m.toolCallId,
            content: m.content,
          },
        ],
      });
      continue;
    }
    if (m.role === "assistant" && m.toolCalls?.length) {
      const content: unknown[] = [];
      if (m.content) content.push({ type: "text", text: m.content });
      for (const tc of m.toolCalls) {
        content.push({
          type: "tool_use",
          id: tc.id,
          name: tc.name,
          input: tc.arguments,
        });
      }
      out.push({ role: "assistant", content });
      continue;
    }
    out.push({ role: m.role, content: m.content });
  }
  return out;
}

function cryptoId(): string {
  return "toolu_" + Math.random().toString(36).slice(2, 12);
}
